/* 151G §2 — CAN A BUTTON CLICK READ THE CLIPBOARD IN THIS BROWSER?
 *
 * The Paste button (151D §4(b)) calls navigator.clipboard.readText() from a
 * click. Three engines, three answers:
 *
 *   Chromium   readText + a queryable 'clipboard-read' permission. A click
 *              reads (after the UA's own prompt); query state 'denied' is the
 *              one no-go, and a prompt DECLINED AT CLICK TIME still surfaces
 *              through the caller's catch (the refusal toast — visible above
 *              every backdrop since §1).
 *   Firefox    no queryable 'clipboard-read' name (query THROWS). Old FF has
 *              no readText at all → unavailable by the API check. FF 125+
 *              ships readText behind its own paste prompt, which a click
 *              satisfies → available.
 *   WebKit     readText exists and the permission name throws, but a plain
 *   (Safari,   button click is REFUSED (NotAllowedError) — WebKit honours
 *   all iOS)   only its own paste affordances (⌘V, the Edit menu, the
 *              callout). This is the ruled case: the button must render
 *              unresponsive with a hover text, never click-then-toast.
 *
 * Detection is therefore three-tiered: no readText → false; WebKit → false
 * (vendor === 'Apple Computer, Inc.' — every browser on iOS is WebKit and
 * inherits the restriction, Chrome reports 'Google Inc.', Firefox '');
 * otherwise true, refined asynchronously to false when a queryable
 * permission answers 'denied'. The refinement only ever DOWNGRADES: a
 * hypothetical WebKit that resolves the query someday still renders the
 * ruled face until that Safari is actually glanced.
 *
 * Keyboard pasting into the field is untouched by all of this — a real ⌘V
 * is the user's own gesture and every engine allows it, which is what the
 * hover text tells the user to do.
 */
import { useEffect, useState } from "react";

export function clipboardReadableSync(): boolean {
  if (typeof navigator === "undefined") return false;
  const c = navigator.clipboard;
  if (!c || typeof c.readText !== "function") return false;
  if (navigator.vendor === "Apple Computer, Inc.") return false;
  return true;
}

export function useClipboardReadable(): boolean {
  const [readable, setReadable] = useState<boolean>(clipboardReadableSync);
  /* Mount-once, deps EMPTY, and the guard reads the pure sync function
     rather than the state — an effect that lists a state variable it sets,
     with a cleanup, is the shape the effect-sets-its-own-dep lint bans
     (brief 78's modal that could not draw itself). The refinement is
     one-shot by nature: the permission verdict does not change under the
     open dialog, and it only ever DOWNGRADES. */
  useEffect(() => {
    if (!clipboardReadableSync()) return;
    const perms = navigator.permissions;
    if (!perms || typeof perms.query !== "function") return;
    let alive = true;
    perms
      .query({ name: "clipboard-read" as PermissionName })
      .then((st) => {
        if (alive && st.state === "denied") setReadable(false);
      })
      .catch(() => {
        /* the permission NAME is unknown here (Firefox) — the sync verdict
           stands */
      });
    return () => {
      alive = false;
    };
  }, []);
  return readable;
}

export default useClipboardReadable;
